import { useBoolean } from '@fluentui/react-hooks'
import {
    Button,
    createTableColumn,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridRow,
    makeStyles,
    mergeClasses,
    Popover,
    PopoverSurface,
    PopoverTrigger,
    shorthands,
    TableColumnDefinition,
    Text,
    Title2,
    tokens,
    Toolbar,
    ToolbarButton,
    ToolbarGroup,
    Tooltip,
} from '@fluentui/react-components'
import * as React from 'react'
import { ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
    AddFilled,
    AddRegular,
    bundleIcon,
    DeleteDismissFilled,
    DeleteDismissRegular,
    PauseFilled,
    PauseRegular,
    PlayFilled,
    PlayRegular,
    SaveFilled,
    SaveRegular,
    SaveSyncFilled,
    SaveSyncRegular,
    TimerFilled,
    TimerRegular,
} from '@fluentui/react-icons'
import * as UUID from 'uuid'
import { DateTime, Interval } from 'luxon'
import { formatTimeInSeconds } from '../../helpers/numberHelper'
import { COMMAND_PREFIX } from '../../pages/Commands'
import { Command } from '../../config/appConfig'
import { TimeMutationDto } from '../../config/registrationConfig'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HammockButtonProps } from '../Overflow/OverflowTabListPanel'
import useLocalStorage from '../../hooks/useLocalStorage'
import Loading from '../Loading'
import FilePicker from '../TagPicker/FilePicker'
import useViewport, { Viewport } from '../../hooks/useViewport'
import AuthorizationContext from '../../context/AuthorizationContext'

const useStyles = makeStyles({
    surface: {
        padding: tokens.spacingVerticalS,
    },
    containerNarrow: {
        maxWidth: '365px',
    },
    containerTiny: {
        maxWidth: '285px',
    },
    toolbar: {
        paddingLeft: '8px',
        paddingRight: '8px',
        justifyContent: 'space-between',
        minWidth: '200px',
    },
    borderBottom: {
        borderBottomWidth: tokens.strokeWidthThin,
        borderBottomStyle: 'solid',
        borderBottomColor: tokens.colorNeutralStroke2,
    },
    timerButton: {
        '> span': {
            marginRight: 'unset',
        },
    },
    gridRow: {
        gap: tokens.spacingVerticalXS,
        ...shorthands.margin(tokens.spacingVerticalXS, 0),
    },
    gridCellTiny: {
        minHeight: '24px',
    },
    gridCell: {
        minHeight: '32px',
    },
    gridCell1Button: {
        minWidth: '32px',
    },
    gridCell1ButtonTiny: {
        minWidth: '24px',
    },
    gridCell2ButtonsTiny: {
        minWidth: '48px',
    },
    gridCell2Buttons: {
        minWidth: '64px',
    },
    gridCellFileId: {
        flex: '1 1 auto',
    },
    gridRowPaused: {
        opacity: '70%',
        '&:hover': {
            opacity: '100%',
        },
    },
    empty: {
        display: 'flex',
        padding: '0px 10px',
        minHeight: '45px',
        alignItems: 'center',
    },
})

type Timer = {
    id: string
    isRunning: boolean
    isSaved: boolean
    intervals: Interval[]
    fileId?: string
}

type TimerJson = {
    id: string
    isRunning: boolean
    isSaved: boolean
    intervals: IntervalJson[]
    fileId?: string
}

type IntervalJson = {
    start: string
    end: string
}

const AddIcon = bundleIcon(AddFilled, AddRegular)
const DismissIcon = bundleIcon(DeleteDismissFilled, DeleteDismissRegular)
const PauseIcon = bundleIcon(PauseFilled, PauseRegular)
const PlayIcon = bundleIcon(PlayFilled, PlayRegular)
const SaveIcon = bundleIcon(SaveFilled, SaveRegular)
const SaveAgainIcon = bundleIcon(SaveSyncFilled, SaveSyncRegular)
const TimerIcon = bundleIcon(TimerFilled, TimerRegular)

const calculateDuration = (timer: Timer): string => {
    return formatTimeInSeconds(
        timer.intervals
            .map(interval => {
                if (interval.end.equals(DateTime.fromISO('3000-01-01').toUTC())) {
                    return Interval.fromDateTimes(interval.start, DateTime.now())
                }
                return interval
            })
            .map(interval => interval.toDuration('seconds').seconds)
            .reduce((a, b) => a + b, 0),
    )
}

export function TimerDuration(props: { timer: Timer }): ReactElement {
    const [duration, setDuration] = useState(() => calculateDuration(props.timer))
    const durationTimeout = useRef<NodeJS.Timeout>()

    useEffect(() => {
        const updateDuration = () => {
            setDuration(() => calculateDuration(props.timer))
            durationTimeout.current = setTimeout(updateDuration, 1000)
        }
        updateDuration()

        return () => durationTimeout.current && clearTimeout(durationTimeout.current)
    }, [props.timer])

    return <div>{duration}</div>
}

export function Timers(props: { buttonProps: HammockButtonProps }): ReactElement {
    const history = useHistory()
    const { host: currentHost } = useContext(AuthorizationContext)
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()

    const [isOpen, { toggle: toggleOpen }] = useBoolean(false)
    const viewportTiny = useMemo<boolean>(() => isViewport('<', Viewport.OutlookWeb), [viewport])
    const viewportNarrow = useMemo<boolean>(() => isViewport('<', Viewport.OutlookNew), [viewport])

    const {
        loaded: loadedTimers,
        values: timers,
        setValues: setTimers,
    } = useLocalStorage<Timer[], TimerJson[]>({
        hostKey: currentHost?.RowKey,
        key: 'timers',
        defaultValue: [],
        fromJson: values =>
            values.map<Timer>(timer => ({
                ...timer,
                intervals: timer.intervals.map<Interval>(interval =>
                    Interval.fromDateTimes(DateTime.fromISO(interval.start).toUTC(), DateTime.fromISO(interval.end).toUTC()),
                ),
            })),
        toJson: values =>
            values.map<TimerJson>(timer => ({
                ...timer,
                intervals: timer.intervals.map<IntervalJson>(interval => ({
                    start: interval.start.toISO(),
                    end: interval.end.toISO(),
                })),
            })),
        load: !!currentHost,
    })

    const addTimer = (): void => {
        setTimers([
            {
                id: UUID.v4(),
                isRunning: true,
                isSaved: false,
                intervals: [Interval.fromDateTimes(DateTime.now().toUTC(), DateTime.fromISO('3000-01-01').toUTC())],
            },
            ...timers,
        ])
    }

    const updateTimer = (timer?: Timer): void => {
        setTimers([
            ...timers.map(curTimer => {
                if (curTimer.id !== timer.id) {
                    return curTimer
                }

                return timer
            }),
        ])
    }

    const pauseTimer = (id?: string): void => {
        const endDateTime = DateTime.now().toUTC()
        setTimers([
            ...timers.map(timer => {
                if (!timer.isRunning || (id !== undefined && timer.id !== id)) {
                    return timer
                }

                const otherIntervals = timer.intervals?.filter(interval => interval.end.diff(DateTime.now().toUTC()).toMillis() < 0)
                const currentInterval = timer.intervals?.find(interval => interval.end.diff(DateTime.now().toUTC()).toMillis() > 0)

                return { ...timer, intervals: [...otherIntervals, Interval.fromDateTimes(currentInterval.start, endDateTime)], isRunning: false }
            }),
        ])
    }

    const playTimer = (id: string): void => {
        const startDateTime = DateTime.now().toUTC()
        const endDateTime = DateTime.fromISO('3000-01-01').toUTC()

        const otherTimers = timers.filter(timer => timer.id !== id)
        const timer = timers.find(timer => timer.id === id)

        setTimers([
            { ...timer, intervals: [...timer.intervals, Interval.fromDateTimes(startDateTime, endDateTime)], isRunning: true },
            ...otherTimers,
        ])
    }

    const saveTimer = (id: string): void => {
        const timer = timers.find(timer => timer.id === id)
        const otherTimers = timers.filter(timer => timer.id !== id)

        if (timer.isRunning) {
            pauseTimer(id)
        }

        const timeMutation: Partial<TimeMutationDto> = {
            fileId: timer.fileId,
            date: DateTime.now().toFormat('yyyy-LL-dd'),
            duration: calculateDuration(timer),
        }
        history.push(`${COMMAND_PREFIX}/${Command.AddTime}`, { data: timeMutation })
        timer.isSaved = true

        setTimers([...otherTimers, timer])
    }

    const deleteTimer = (id?: string): void => {
        setTimers([...timers.filter(timer => id !== undefined && timer.id !== id)])
    }

    const timerColumns: TableColumnDefinition<Timer>[] = [
        createTableColumn<Timer>({
            columnId: 'actions',
            renderCell: timer => {
                return (
                    <>
                        <Tooltip
                            content={timer.isRunning ? translate('timers.timer.pause') : translate('timers.timer.play')}
                            relationship="label"
                            withArrow
                        >
                            <Button
                                appearance={'subtle'}
                                size={viewportTiny ? 'small' : 'medium'}
                                icon={timer.isRunning ? <PauseIcon /> : <PlayIcon />}
                                onClick={() => (timer.isRunning ? pauseTimer(timer.id) : playTimer(timer.id))}
                            />
                        </Tooltip>
                        <Tooltip content={translate('timers.timer.save')} relationship="label" withArrow>
                            <Button
                                appearance={'subtle'}
                                size={viewportTiny ? 'small' : 'medium'}
                                icon={timer.isSaved ? <SaveAgainIcon /> : <SaveIcon />}
                                onClick={() => {
                                    saveTimer(timer.id)
                                    toggleOpen()
                                }}
                            />
                        </Tooltip>
                    </>
                )
            },
        }),
        createTableColumn<Timer>({
            columnId: 'fileId',
            renderCell: timer => {
                return (
                    <FilePicker
                        appearance={'underline'}
                        singleSelect
                        required
                        selectedOptions={timer.fileId ? [timer.fileId] : []}
                        onOptionsSelect={(_, options) => {
                            updateTimer({ ...timer, fileId: options?.[0] })
                        }}
                        placeholderPrefixKey={'base.tagpicker.placeholder.prefix_choose'}
                        placeholderKey={''}
                    />
                )
            },
        }),
        createTableColumn<Timer>({
            columnId: 'duration',
            renderCell: timer => {
                return <TimerDuration timer={timer} />
            },
        }),
        createTableColumn<Timer>({
            columnId: 'delete',
            renderCell: timer => {
                return (
                    <Tooltip content={translate('timers.timer.delete')} relationship="label" withArrow>
                        <Button
                            appearance={'subtle'}
                            size={viewportTiny ? 'small' : 'medium'}
                            icon={<DismissIcon />}
                            onClick={() => deleteTimer(timer.id)}
                        />
                    </Tooltip>
                )
            },
        }),
    ]

    if (!loadedTimers) {
        return <Loading label={''} size={'tiny'} />
    }

    return (
        <Popover withArrow trapFocus={true} open={isOpen} onOpenChange={toggleOpen} positioning={'below-end'}>
            <PopoverTrigger disableButtonEnhancement>
                <Tooltip relationship={'label'} content={translate('timers.title')}>
                    <Button {...props.buttonProps} className={mergeClasses(styles.timerButton, props.buttonProps?.className)} icon={<TimerIcon />}>
                        {timers.filter(timer => timer.isRunning).length > 0 && (
                            <Text size={200}>{timers.filter(timer => timer.isRunning).length}</Text>
                        )}
                    </Button>
                </Tooltip>
            </PopoverTrigger>
            <PopoverSurface tabIndex={-1} className={styles.surface}>
                <div className={mergeClasses(viewportNarrow && styles.containerNarrow, viewportTiny && styles.containerTiny)}>
                    <Title2 align="center">{translate('timers.title')}</Title2>
                    <div>
                        <Toolbar size="small" className={mergeClasses(styles.toolbar, styles.borderBottom)}>
                            <ToolbarGroup role="presentation">
                                <Tooltip content={translate('timers.add')} relationship="label" withArrow>
                                    <ToolbarButton icon={<AddIcon />} onClick={addTimer} />
                                </Tooltip>
                                <Tooltip content={translate('timers.pause')} relationship="label" withArrow>
                                    <ToolbarButton icon={<PauseIcon />} onClick={() => pauseTimer()} />
                                </Tooltip>
                            </ToolbarGroup>
                            <ToolbarGroup role="presentation">
                                <Tooltip content={translate('timers.delete')} relationship="label" withArrow>
                                    <ToolbarButton icon={<DismissIcon />} onClick={() => deleteTimer()} />
                                </Tooltip>
                            </ToolbarGroup>
                        </Toolbar>
                        <>
                            {(timers || []).length === 0 && (
                                <div className={mergeClasses(styles.empty, styles.borderBottom)}>{translate('timers.empty')}</div>
                            )}

                            {(timers || []).length > 0 && (
                                <DataGrid items={timers} columns={timerColumns} getRowId={item => item.id}>
                                    <DataGridBody<Timer>>
                                        {({ item: timer, rowId }) => (
                                            <DataGridRow<Timer>
                                                key={rowId}
                                                className={mergeClasses(styles.gridRow, !timer.isRunning && styles.gridRowPaused)}
                                            >
                                                {props => (
                                                    <DataGridCell
                                                        key={rowId}
                                                        className={mergeClasses(
                                                            styles.gridCell,
                                                            viewportTiny && styles.gridCellTiny,
                                                            props.columnId === 'actions' && styles.gridCell2Buttons,
                                                            viewportTiny && props.columnId === 'actions' && styles.gridCell2ButtonsTiny,
                                                            props.columnId === 'fileId' && styles.gridCellFileId,
                                                            props.columnId === 'duration' && styles.gridCellFileId,
                                                            props.columnId === 'delete' && styles.gridCell1Button,
                                                            viewportTiny && props.columnId === 'delete' && styles.gridCell1ButtonTiny,
                                                        )}
                                                    >
                                                        {props.renderCell(timer)}
                                                    </DataGridCell>
                                                )}
                                            </DataGridRow>
                                        )}
                                    </DataGridBody>
                                </DataGrid>
                            )}
                        </>
                    </div>
                </div>
            </PopoverSurface>
        </Popover>
    )
}
